if (!customElements.get('cross-sell-tab-nav')) {
  class CrossSellTabNav extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.tablist__scroller');
      this.tabList = this.querySelector('[role="tablist"]');
      this.track = this.querySelector('.cross-sell-products__tab-track');
      this.prevBtn = this.querySelector('.cross-sell-products__tab-nav-btn--prev');
      this.nextBtn = this.querySelector('.cross-sell-products__tab-nav-btn--next');
      this.viewAllLink = this.closest('[data-cross-sell-products-root]')?.querySelector('[data-cross-sell-view-all]');

      if (!this.scroller || !this.prevBtn || !this.nextBtn) return;

      this.rtl = document.dir === 'rtl';
      this.update = this.update.bind(this);
      this.updateViewAllLink = this.updateViewAllLink.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleTabInteraction = this.handleTabInteraction.bind(this);

      const scrollEvent = 'onscrollend' in window ? 'scrollend' : 'scroll';
      this.scroller.addEventListener(scrollEvent, this.update, { passive: true });
      this.tabList?.addEventListener('click', this.handleTabInteraction);
      this.tabList?.addEventListener('keydown', this.handleTabInteraction);
      window.addEventListener('on:debounced-resize', this.update, { passive: true });
      this.prevBtn.addEventListener('click', this.handlePrevClick);
      this.nextBtn.addEventListener('click', this.handleNextClick);

      requestAnimationFrame(() => {
        this.update();
        this.updateViewAllLink();
      });
    }

    disconnectedCallback() {
      if (!this.scroller) return;

      const scrollEvent = 'onscrollend' in window ? 'scrollend' : 'scroll';
      this.scroller.removeEventListener(scrollEvent, this.update);
      this.tabList?.removeEventListener('click', this.handleTabInteraction);
      this.tabList?.removeEventListener('keydown', this.handleTabInteraction);
      window.removeEventListener('on:debounced-resize', this.update);
      this.prevBtn?.removeEventListener('click', this.handlePrevClick);
      this.nextBtn?.removeEventListener('click', this.handleNextClick);
    }

    handlePrevClick() {
      this.scrollByPage(-1);
    }

    handleNextClick() {
      this.scrollByPage(1);
    }

    handleTabInteraction() {
      setTimeout(this.updateViewAllLink, 0);
    }

    updateViewAllLink() {
      if (!this.viewAllLink) return;

      const activeTab = this.tabList?.querySelector('[role="tab"][aria-selected="true"]');
      const viewAllUrl = activeTab?.dataset.crossSellViewAllUrl;
      const viewAllTitle = activeTab?.dataset.crossSellViewAllTitle || '';
      const labelTemplate = this.viewAllLink.dataset.crossSellViewAllLabel || this.viewAllLink.textContent.trim();
      const label = labelTemplate.replace(/\[collection_title\]/g, viewAllTitle);

      this.viewAllLink.hidden = !viewAllUrl;
      if (viewAllUrl) {
        this.viewAllLink.href = viewAllUrl;
        this.viewAllLink.textContent = label;
      } else {
        this.viewAllLink.removeAttribute('href');
      }
    }

    scrollByPage(direction) {
      const amount = this.scroller.clientWidth * 0.75 * direction;
      this.scroller.scrollBy({ left: this.rtl ? -amount : amount, behavior: 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      const tolerance = 3;
      const maxScroll = Math.max(0, scrollWidth - clientWidth);
      const hasOverflow = maxScroll > tolerance;
      const scrollPosition = Math.min(maxScroll, Math.max(0, Math.abs(scrollLeft)));
      const atStart = !hasOverflow || scrollPosition <= tolerance;
      const atEnd = !hasOverflow || maxScroll - scrollPosition <= tolerance;

      this.prevBtn.hidden = !hasOverflow;
      this.nextBtn.hidden = !hasOverflow;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;

      if (this.track) {
        this.track.classList.toggle('has-fade-start', !atStart);
        this.track.classList.toggle('has-fade-end', !atEnd);
      }
    }
  }

  customElements.define('cross-sell-tab-nav', CrossSellTabNav);
}

if (!customElements.get('cross-sell-product-list-loader')) {
  class CrossSellProductListLoader extends HTMLElement {
    connectedCallback() {
      this.loaded = false;
      this.requestId = 0;
      this.load = this.load.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.observe = this.observe.bind(this);

      document.addEventListener('on:variant:change', this.handleVariantChange);

      this.observe();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.handleVariantChange);
      this.observer?.disconnect();
    }

    observe() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.load();
          }
        }, { rootMargin: '700px 0px' });
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    resumeAfterFailure() {
      if ('IntersectionObserver' in window) this.observe();
    }

    restartAfterStaleResponse() {
      if ('IntersectionObserver' in window) {
        this.observe();
      } else {
        setTimeout(this.load, 0);
      }
    }

    handleVariantChange(evt) {
      const variantId = evt.detail?.variant?.id;
      if (!variantId) return;

      this.dataset.variantId = variantId;
      if (!this.loaded) this.requestId += 1;
    }

    async load() {
      if (this.loaded || !this.dataset.sectionId || !this.dataset.productUrl) return;
      this.loaded = true;
      this.observer?.disconnect();
      this.requestId += 1;
      const requestId = this.requestId;

      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (this.dataset.variantId) url.searchParams.set('variant', this.dataset.variantId);

      try {
        const response = await fetch(url.toString(), {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (!response.ok) throw new Error(response.status);

        const html = await response.text();
        if (requestId !== this.requestId) {
          this.loaded = false;
          this.restartAfterStaleResponse();
          return;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-cross-sell-product-list-content]');
        if (!content) {
          this.loaded = false;
          this.resumeAfterFailure();
          return;
        }

        this.replaceWith(content);
      } catch (error) {
        this.loaded = false;
        this.resumeAfterFailure();
        console.log(error); // eslint-disable-line
      }
    }
  }

  customElements.define('cross-sell-product-list-loader', CrossSellProductListLoader);
}
